import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Compass, MapPinOff, RefreshCw } from "lucide-react";
import { getPublicEvents, getPublicServices } from "@/lib/entitiesStore";
import { loadGoogleMaps, reverseGeocode, isGoogleMapsEnabled } from "@/lib/googleMaps";
import { setLastGeo } from "@/lib/userGeoStore";
import InitialAvatar from "@/components/InitialAvatar";

// Haversine distance in km between two lat/lng pairs (returns null if any
// coord is missing).
const haversineKm = (a, b) => {
    if (!a || !b) return null;
    const aLat = Number(a.lat), aLng = Number(a.lng);
    const bLat = Number(b.lat), bLng = Number(b.lng);
    if (!Number.isFinite(aLat) || !Number.isFinite(aLng)) return null;
    if (!Number.isFinite(bLat) || !Number.isFinite(bLng)) return null;
    const R = 6371; // km
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const s =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};

// Deterministic pseudo-distance (fallback when an entity has no coords yet)
const distanceFor = (seed) => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    const km = (h % 120) / 10 + 0.3; // 0.3 – 12.3 km
    return km.toFixed(1);
};

const PERMISSION_STATES = {
    PROMPT: "prompt",      // haven't asked yet / waiting on the user
    REQUESTING: "requesting",
    GRANTED: "granted",
    DENIED: "denied",
    UNAVAILABLE: "unavailable",
};

const NearMe = () => {
    const navigate = useNavigate();
    const [permState, setPermState] = useState(PERMISSION_STATES.PROMPT);
    const [coords, setCoords] = useState(null); // { lat, lng, accuracy }
    const [placeLabel, setPlaceLabel] = useState(null);

    const requestLocation = () => {
        if (!("geolocation" in navigator)) {
            setPermState(PERMISSION_STATES.UNAVAILABLE);
            return;
        }
        setPermState(PERMISSION_STATES.REQUESTING);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const c = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    accuracy: pos.coords.accuracy,
                };
                setCoords(c);
                setLastGeo(c.lat, c.lng); // cache for create flows
                setPermState(PERMISSION_STATES.GRANTED);
            },
            (err) => {
                if (err.code === err.PERMISSION_DENIED) {
                    setPermState(PERMISSION_STATES.DENIED);
                } else {
                    setPermState(PERMISSION_STATES.UNAVAILABLE);
                }
            },
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
        );
    };

    useEffect(() => { requestLocation(); }, []);

    // Resolve coordinates → human label via Google Maps reverse geocode
    // (only when a key is configured — falls back to "lat, lng" otherwise).
    useEffect(() => {
        if (!coords) return;
        let cancelled = false;
        (async () => {
            const label = await reverseGeocode(coords.lat, coords.lng);
            if (!cancelled) {
                setPlaceLabel(
                    label || `${coords.lat.toFixed(3)}, ${coords.lng.toFixed(3)}`
                );
            }
        })();
        return () => { cancelled = true; };
    }, [coords]);

    const items = useMemo(() => {
        if (permState !== PERMISSION_STATES.GRANTED) return [];
        const distance = (entity, fallbackSeed) => {
            const real = haversineKm(coords, entity);
            if (real != null) return +real.toFixed(1);
            return +distanceFor(fallbackSeed);
        };
        const evts = getPublicEvents().map((e) => ({
            type: "event", ref: e, d: distance(e, e.id + (e.title || "")),
        }));
        const svcs = getPublicServices().map((s) => ({
            type: "service", ref: s, d: distance(s, s.id + (s.name || "")),
        }));
        return [...evts, ...svcs].sort((a, b) => a.d - b.d);
    }, [permState, coords]);

    return (
        <div className="screen" data-testid="near-me-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        data-testid="near-me-back-btn"
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95 transition-[transform,background-color] duration-150"
                        aria-label="Back"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div className="flex-1">
                        <h1 className="font-display text-[16px] font-semibold">Near Me</h1>
                        <p
                            className="text-[11.5px] text-muted-foreground flex items-center gap-1"
                            data-testid="near-me-location-label"
                        >
                            <MapPin size={11} strokeWidth={2.6} />
                            {permState === PERMISSION_STATES.GRANTED && placeLabel
                                ? placeLabel
                                : permState === PERMISSION_STATES.GRANTED
                                ? "Locating you…"
                                : permState === PERMISSION_STATES.REQUESTING
                                ? "Requesting your location…"
                                : permState === PERMISSION_STATES.DENIED
                                ? "Location permission denied"
                                : permState === PERMISSION_STATES.UNAVAILABLE
                                ? "Location unavailable"
                                : "Tap allow to share your location"}
                        </p>
                    </div>
                    <span className="h-10 w-10" />
                </div>
            </header>

            {(permState === PERMISSION_STATES.DENIED ||
                permState === PERMISSION_STATES.UNAVAILABLE) && (
                <div className="px-4 pt-6" data-testid="near-me-permission-required">
                    <div className="rounded-2xl border border-border bg-card p-5 text-center">
                        <span className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground mb-3">
                            <MapPinOff size={20} />
                        </span>
                        <h2 className="font-display text-[15px] font-semibold">
                            Location access is needed
                        </h2>
                        <p className="text-[12.5px] text-muted-foreground mt-1.5 max-w-[300px] mx-auto">
                            Location permission is required to show nearby people and services.
                            {permState === PERMISSION_STATES.DENIED &&
                                " Please enable location access in your browser settings, then try again."}
                        </p>
                        <button
                            type="button"
                            data-testid="near-me-request-location"
                            onClick={requestLocation}
                            className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-[13px] font-medium"
                        >
                            <RefreshCw size={13} />
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {(permState === PERMISSION_STATES.PROMPT ||
                permState === PERMISSION_STATES.REQUESTING) && (
                <div className="px-4 pt-6" data-testid="near-me-loading">
                    <div className="rounded-2xl border border-border bg-card p-5 text-center">
                        <span className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3 animate-pulse">
                            <Compass size={20} />
                        </span>
                        <p className="text-[12.5px] text-muted-foreground">
                            Asking for your location…
                        </p>
                    </div>
                </div>
            )}

            {permState === PERMISSION_STATES.GRANTED && coords && (
                <>
                    <RealMap coords={coords} placeLabel={placeLabel} entities={items} />

                    <div className="flex-1 overflow-y-auto no-scrollbar px-4 pt-4 pb-8">
                        <h2 className="px-1 mb-2 font-display text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                            Nearby ({items.length})
                        </h2>
                        {items.length === 0 ? (
                            <div
                                className="rounded-2xl border border-dashed border-border bg-card/40 p-6 text-center text-[12.5px] text-muted-foreground"
                                data-testid="near-me-empty"
                            >
                                Nothing nearby yet — be the first to publish a service or event in your area.
                            </div>
                        ) : (
                            <ul className="space-y-2.5" data-testid="near-me-list">
                                {items.map((it, idx) => (
                                    <NearbyRow key={`${it.type}-${it.ref.id}`} item={it} idx={idx} />
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const NearbyRow = ({ item, idx }) => {
    const navigate = useNavigate();
    const { type, ref, d } = item;
    const title = type === "event" ? ref.title : ref.name;
    const subtitle =
        type === "event"
            ? `${ref.category || "Event"} · ${ref.location || ""}`
            : `${ref.category || "Service"} · ${ref.location || ""}`;

    const onClick = () => {
        if (type === "event") navigate(`/events/${ref.id}`);
        else navigate(`/services/${ref.id}`);
    };

    return (
        <li>
            <button
                data-testid={`nearby-row-${idx}`}
                onClick={onClick}
                className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card shadow-soft hover:bg-accent/40 active:scale-[0.99] transition-[transform,background-color] duration-150 text-left"
            >
                <span
                    className="h-11 w-11 rounded-xl overflow-hidden bg-muted shrink-0"
                    aria-hidden
                >
                    {ref.image ? (
                        <img
                            src={ref.image}
                            alt=""
                            loading="lazy"
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <InitialAvatar name={title} size={44} />
                    )}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="font-display text-[13.5px] font-semibold truncate">{title}</div>
                    <div className="text-[11.5px] text-muted-foreground truncate">{subtitle}</div>
                </div>
                <span className="text-[11px] font-medium text-primary shrink-0">
                    {d} km
                </span>
            </button>
        </li>
    );
};

export default NearMe;

const RealMap = ({ coords, placeLabel, entities }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const [error, setError] = useState(null);
    const enabled = isGoogleMapsEnabled();

    useEffect(() => {
        if (!enabled) {
            setError("Map is currently unavailable. Your location was detected successfully.");
            return undefined;
        }
        let cancelled = false;
        loadGoogleMaps()
            .then((g) => {
                if (cancelled || !g || !containerRef.current) return;
                const center = { lat: coords.lat, lng: coords.lng };
                if (!mapRef.current) {
                    mapRef.current = new g.maps.Map(containerRef.current, {
                        center,
                        zoom: 14,
                        disableDefaultUI: true,
                        zoomControl: true,
                        gestureHandling: "greedy",
                        styles: [
                            { featureType: "poi", stylers: [{ visibility: "off" }] },
                        ],
                    });
                } else {
                    mapRef.current.setCenter(center);
                }

                // Clear previous markers
                for (const m of markersRef.current) m.setMap(null);
                markersRef.current = [];

                // User marker
                const youMarker = new g.maps.Marker({
                    position: center,
                    map: mapRef.current,
                    title: placeLabel ? `You · ${placeLabel}` : "You",
                    icon: {
                        path: g.maps.SymbolPath.CIRCLE,
                        scale: 9,
                        fillColor: "#C39A3D",
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 3,
                    },
                });
                markersRef.current.push(youMarker);

                // Entity markers (only those with coords)
                for (const it of entities || []) {
                    const ref = it.ref;
                    if (!Number.isFinite(ref?.lat) || !Number.isFinite(ref?.lng)) continue;
                    const m = new g.maps.Marker({
                        position: { lat: ref.lat, lng: ref.lng },
                        map: mapRef.current,
                        title: it.type === "event" ? ref.title : ref.name,
                    });
                    markersRef.current.push(m);
                }
            })
            .catch(() => {
                if (cancelled) return;
                setError("Map is currently unavailable. Your location was detected successfully.");
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, coords.lat, coords.lng, placeLabel, entities]);

    if (!enabled || error) {
        return (
            <div className="px-4 pt-4">
                <div
                    data-testid="near-me-map-fallback"
                    className="rounded-2xl border border-border bg-card p-5 text-center"
                >
                    <span className="mx-auto h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                        <Compass size={20} />
                    </span>
                    <h3 className="font-display text-[14px] font-semibold">
                        Map is currently unavailable
                    </h3>
                    <p className="text-[12.5px] text-muted-foreground mt-1">
                        Your location was detected successfully.
                        {placeLabel ? (
                            <>
                                <br />
                                <span className="text-foreground font-medium">{placeLabel}</span>
                            </>
                        ) : null}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 pt-4">
            <div
                ref={containerRef}
                data-testid="near-me-map"
                className="h-[260px] rounded-2xl overflow-hidden border border-border bg-muted"
            />
        </div>
    );
};
